import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, retry, timer } from 'rxjs';
import { environment } from '../../environments/environment';

/** Standard API response envelope used across the app: branch on `success`. */
export interface ApiEnvelope<T = any> {
  success: boolean;
  message: string;
  statusCode: number;
  data: T;
}

// ===================== models =====================

export interface PaymentGateway {
  id: number;
  gatewayCode: string;
  gatewayName: string;
  baseUrl?: string | null;
  logoUrl?: string | null;
  isActive: boolean;
  createdDate?: string | null;
  updatedDate?: string | null;
}

export type IntegrationScope = 'GLOBAL' | 'SPECIFIC';

export interface PaymentIntegration {
  id: number;
  gatewayId: number;
  partnerId?: number | null;
  integrationType: IntegrationScope;
  merchantId?: string | null;
  apiKey?: string | null;
  apiSecret?: string | null;
  authToken?: string | null;
  webhookSecret?: string | null;
  isLive: boolean;
  extraConfigJson?: string | null;
  isActive: boolean;
  // joined / read-only
  gatewayCode?: string | null;
  gatewayName?: string | null;
  partnerName?: string | null;
  partnerCode?: string | null;
}

export interface PaymentType {
  id: number;
  category: string;
  typeCode: string;
  typeName: string;
  displayOrder: number;
  isActive: boolean;
}

export type PayerType = 'PARTNER' | 'CLIENT';

export interface MemberPayment {
  id: number;
  memberId: number;
  memberUniqueId?: string | null;
  paymentUniqueId: string;
  gatewayId?: number | null;
  integrationId?: number | null;
  amount: number;
  currency?: string | null;
  paymentMode?: string | null;
  payerType: PayerType;
  partnerId?: number | null;
  clientRef?: string | null;
  paymentStatus: string;
  gatewayOrderId?: string | null;
  gatewayPaymentId?: string | null;
  gatewayResponseJson?: string | null;
  failureReason?: string | null;
  paidAt?: string | null;
  createdDate?: string | null;
  // joined / read-only
  memberName?: string | null;
  gatewayName?: string | null;
}

/** The three PaymentTypeMaster categories — also the dropdown source for Collect Payment. */
export const PAYMENT_CATEGORIES = ['PAYMENT_STATUS', 'PAYMENT_MODE', 'PAYER_TYPE'] as const;
export type PaymentCategory = (typeof PAYMENT_CATEGORIES)[number];

/** Modes that bypass the gateway — recorded + marked SUCCESS directly, no Razorpay popup. */
export const OFFLINE_PAYMENT_MODES = ['CASH', 'CHEQUE'];

/** Body for POST /api/Razorpay/CreateOrder. `amount` is in rupees (backend converts to paise). */
export interface CreateRazorpayOrder {
  memberId: number;
  amount: number;
  currency?: string;
  paymentMode?: string | null;
  payerType: PayerType;
  partnerId?: number | null;
  clientRef?: string | null;
}

/** `data` returned by POST /api/Razorpay/CreateOrder — everything needed to open Checkout. */
export interface RazorpayCheckout {
  paymentId: number;
  paymentUniqueId: string;
  keyId: string; // publishable key_id — safe in the browser
  orderId: string; // "order_..."
  amount: number; // paise
  currency: string;
  name: string;
  description: string;
}

/**
 * `data` from POST /api/MemberPayment/Start — one shape covering every gateway.
 *
 * `gateway` is the discriminator. RAZORPAY fills the Checkout fields and opens in place; GENERALI
 * fills `postUrl` + `fields`, which the browser submits as a form and leaves the page for.
 */
export interface StartedPayment {
  gateway: 'RAZORPAY' | 'GENERALI' | string;
  paymentId: number;
  paymentUniqueId: string;
  memberId?: number;
  amount: number; // RAZORPAY: paise (Checkout's unit). GENERALI: rupees.
  currency: string;

  // --- RAZORPAY ---
  keyId?: string;
  orderId?: string;
  name?: string;
  description?: string;

  // --- GENERALI (redirect) ---
  postUrl?: string;
  fields?: Record<string, string>;
}

/**
 * Hand the browser over to a redirect gateway.
 *
 * A real form POST, not fetch(): the gateway expects a top-level navigation carrying the fields,
 * and the customer has to end up on its page. The payment row is already recorded PENDING before
 * this runs, so the gateway's callback settles it whether or not this tab survives the round trip.
 *
 * `target` names a window to post INTO — pass a popup's name to keep the app on screen behind it,
 * or leave it out to navigate this tab. It is deliberately not an iframe: payment pages routinely
 * refuse to be framed (X-Frame-Options), and a blank modal is worse than an honest redirect.
 *
 * Returns false when the payload is unusable, so the caller can report a failure rather than
 * appearing to do nothing.
 */
export function postToGateway(
  postUrl?: string | null,
  fields?: Record<string, string> | null,
  target?: string,
): boolean {
  if (!postUrl || !fields) return false;

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = postUrl;
  if (target) form.target = target;
  form.style.display = 'none';

  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value ?? '';
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
  form.remove();
  return true;
}

/** Name of the popup a redirect gateway is driven in. Shared so the form can target it. */
export const GATEWAY_WINDOW_NAME = 'arogyaGatewayWindow';

/**
 * Open the centred popup a redirect gateway will run in.
 *
 * MUST be called synchronously from the click that starts the payment — a window.open issued
 * later, from an HTTP callback, is treated as unsolicited and blocked. So it opens blank first
 * and the gateway form is posted into it once the server replies.
 *
 * Returns null when the browser blocked it, which the caller should treat as "fall back to
 * navigating this tab" rather than as a failure.
 */
export function openGatewayWindow(): Window | null {
  try {
    const w = 520;
    const h = 680;
    const left = Math.max(0, (window.screen.width - w) / 2);
    const top = Math.max(0, (window.screen.height - h) / 2);
    const win = window.open(
      '',
      GATEWAY_WINDOW_NAME,
      `popup=yes,width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`,
    );
    if (win) {
      // Something to look at during the round trip to our API; the gateway replaces it.
      win.document.write(
        '<!doctype html><title>Opening secure payment…</title>' +
          '<body style="font:15px system-ui,sans-serif;display:grid;place-items:center;' +
          'height:100vh;margin:0;color:#334155">Opening secure payment…</body>',
      );
    }
    return win;
  } catch {
    return null;
  }
}

/**
 * Generali sub-gateway selector (`paymentOption`), from their integration spec.
 * The integration's own default is used when nothing is passed.
 */
export const GENERALI_PAYMENT_OPTION = { paytm: '1', hdfc: '2', payu: '3' } as const;

/**
 * Generali's optional `method` hint, added in spec v1.42.
 *
 * Sent alongside PaymentOption 3 (PayU) it opens the gateway straight on UPI instead of PayU's
 * method chooser; the server drops it for any other option. `upi` is the only value the spec
 * defines. Leave it unset — which is the current default everywhere — and the customer gets the
 * normal chooser exactly as before.
 */
export const GENERALI_METHOD = { upi: 'upi' } as const;

/** `data` from GET /api/MemberPayment/Method — what would happen if Pay were pressed now. */
export interface PaymentMethodInfo {
  memberId: number;
  gateway: string;
  /** True when paying takes the customer off-site instead of opening a popup. */
  redirect: boolean;
  amount: number | null;
}

/**
 * Human name for a gateway code. Buttons used to be labelled "Pay with Razorpay" unconditionally,
 * which became a lie the moment a partner was assigned anything else — so the label follows the
 * gateway that will actually open. An unrecognised code falls back to a name-free label rather
 * than guessing.
 */
export function gatewayLabel(code?: string | null): string {
  switch ((code || '').toUpperCase()) {
    case 'RAZORPAY': return 'Razorpay';
    case 'GENERALI': return 'Generali Central';
    default: return '';
  }
}

/** Mirrors PaymentReceiptData (server). Reads may come back PascalCase — mapReceipt normalizes them. */
export interface ReceiptData {
  // payment
  paymentId: number;
  paymentUniqueId: string;
  receiptNo: string; // = paymentUniqueId (the display label)
  paymentStatus: string; // receipt actions enabled only when 'SUCCESS'
  amount: number;
  currency: string; // 'INR'
  paymentMode?: string | null;
  gatewayName?: string | null;
  gatewayOrderId?: string | null;
  gatewayPaymentId?: string | null;
  paidAt?: string | null;
  createdDate: string;
  // member (billed to)
  memberId: number;
  memberName?: string | null;
  memberUniqueId?: string | null;
  memberEmail?: string | null;
  memberMobile?: string | null;
  memberAddress?: string | null;
  memberCity?: string | null;
  memberState?: string | null;
  memberPincode?: string | null;
  // policy / premium breakdown
  masterPolicyName?: string | null;
  sumInsured?: number | null;
  basePremium?: number | null; // ⚠ ALREADY INCLUDES doctorConsultantAMT — split it out for display
  doctorConsultantAMT?: number | null; // doctor-consultant add-on; only charged when > 0
  gstPer?: number | null;
  gstAmt?: number | null;
  premiumWithGST?: number | null;
  grossAmt?: number | null;
  // partner (issuing channel)
  partnerName?: string | null;
  partnerCode?: string | null;
  // issuer block (ReceiptSettings row resolved server-side; logoUrl is a URL or data: URI)
  issuer?: ReceiptIssuer | null;
}

/** Dynamic "Issued By" block from the ReceiptSettings master — same source the PDF uses. */
export interface ReceiptIssuer {
  name: string;
  address?: string | null;
  email?: string | null;
  phone?: string | null;
  gstin?: string | null;
  /** Renderable as-is in an <img>: http(s) URL or a base64 data: URI inlined by the server. */
  logoUrl?: string | null;
}

/** The block ConfirmPayment / Razorpay Verify / UpdateStatus return on SUCCESS. */
export interface ReceiptRef {
  available: boolean;
  paymentId: number;
  downloadUrl: string; // relative "/api/PaymentReceipt/Download?paymentId=55" — DON'T use as a raw href
  htmlUrl: string; // relative "/api/PaymentReceipt/Html?paymentId=55"
}

// ===================== PascalCase → camelCase normalization =====================
// Reads can come back PascalCase; writes accept camelCase. Map both on the way in.

function pick<T = any>(x: any, ...keys: string[]): T | undefined {
  for (const k of keys) if (x?.[k] != null) return x[k] as T;
  return undefined;
}
function asBool(v: any, dflt = false): boolean {
  if (v == null) return dflt;
  return v === true || v === 'true' || v === 1 || v === '1';
}
function asNum(v: any, dflt = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : dflt;
}
/** Like pick, but coerces to a number — and returns null when absent (so callers can fall back). */
function pickNum(x: any, ...keys: string[]): number | null {
  const v = pick(x, ...keys);
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function mapGateway(x: any): PaymentGateway {
  return {
    id: asNum(pick(x, 'Id', 'id')),
    gatewayCode: pick(x, 'GatewayCode', 'gatewayCode') ?? '',
    gatewayName: pick(x, 'GatewayName', 'gatewayName') ?? '',
    baseUrl: pick(x, 'BaseUrl', 'baseUrl') ?? null,
    logoUrl: pick(x, 'LogoUrl', 'logoUrl') ?? null,
    isActive: asBool(pick(x, 'IsActive', 'isActive'), true),
    createdDate: pick(x, 'CreatedDate', 'createdDate') ?? null,
    updatedDate: pick(x, 'UpdatedDate', 'updatedDate') ?? null,
  };
}

function mapIntegration(x: any): PaymentIntegration {
  return {
    id: asNum(pick(x, 'Id', 'id')),
    gatewayId: asNum(pick(x, 'GatewayId', 'gatewayId')),
    partnerId: pick<number>(x, 'PartnerId', 'partnerId') ?? null,
    integrationType: (pick(x, 'IntegrationType', 'integrationType') ?? 'GLOBAL') as IntegrationScope,
    merchantId: pick(x, 'MerchantId', 'merchantId') ?? null,
    apiKey: pick(x, 'ApiKey', 'apiKey') ?? null,
    apiSecret: pick(x, 'ApiSecret', 'apiSecret') ?? null,
    authToken: pick(x, 'AuthToken', 'authToken') ?? null,
    webhookSecret: pick(x, 'WebhookSecret', 'webhookSecret') ?? null,
    isLive: asBool(pick(x, 'IsLive', 'isLive'), false),
    extraConfigJson: pick(x, 'ExtraConfigJson', 'extraConfigJson') ?? null,
    isActive: asBool(pick(x, 'IsActive', 'isActive'), true),
    gatewayCode: pick(x, 'GatewayCode', 'gatewayCode') ?? null,
    gatewayName: pick(x, 'GatewayName', 'gatewayName') ?? null,
    partnerName: pick(x, 'PartnerName', 'partnerName') ?? null,
    partnerCode: pick(x, 'PartnerCode', 'partnerCode') ?? null,
  };
}

function mapType(x: any): PaymentType {
  return {
    id: asNum(pick(x, 'Id', 'id')),
    category: pick(x, 'Category', 'category') ?? '',
    typeCode: pick(x, 'TypeCode', 'typeCode') ?? '',
    typeName: pick(x, 'TypeName', 'typeName') ?? '',
    displayOrder: asNum(pick(x, 'DisplayOrder', 'displayOrder')),
    isActive: asBool(pick(x, 'IsActive', 'isActive'), true),
  };
}

function mapRzpCheckout(x: any): RazorpayCheckout {
  return {
    paymentId: asNum(pick(x, 'PaymentId', 'paymentId')),
    paymentUniqueId: pick(x, 'PaymentUniqueId', 'paymentUniqueId') ?? '',
    keyId: pick(x, 'KeyId', 'keyId') ?? '',
    orderId: pick(x, 'OrderId', 'orderId') ?? '',
    amount: asNum(pick(x, 'Amount', 'amount')),
    currency: pick(x, 'Currency', 'currency') ?? 'INR',
    name: pick(x, 'Name', 'name') ?? '',
    description: pick(x, 'Description', 'description') ?? '',
  };
}

function mapPayment(x: any): MemberPayment {
  return {
    id: asNum(pick(x, 'Id', 'id')),
    memberId: asNum(pick(x, 'MemberId', 'memberId')),
    memberUniqueId: pick(x, 'MemberUniqueId', 'memberUniqueId') ?? null,
    paymentUniqueId: pick(x, 'PaymentUniqueId', 'paymentUniqueId') ?? '',
    gatewayId: pick<number>(x, 'GatewayId', 'gatewayId') ?? null,
    integrationId: pick<number>(x, 'IntegrationId', 'integrationId') ?? null,
    amount: asNum(pick(x, 'Amount', 'amount')),
    currency: pick(x, 'Currency', 'currency') ?? 'INR',
    paymentMode: pick(x, 'PaymentMode', 'paymentMode') ?? null,
    payerType: (pick(x, 'PayerType', 'payerType') ?? 'CLIENT') as PayerType,
    partnerId: pick<number>(x, 'PartnerId', 'partnerId') ?? null,
    clientRef: pick(x, 'ClientRef', 'clientRef') ?? null,
    paymentStatus: pick(x, 'PaymentStatus', 'paymentStatus') ?? '',
    gatewayOrderId: pick(x, 'GatewayOrderId', 'gatewayOrderId') ?? null,
    gatewayPaymentId: pick(x, 'GatewayPaymentId', 'gatewayPaymentId') ?? null,
    gatewayResponseJson: pick(x, 'GatewayResponseJson', 'gatewayResponseJson') ?? null,
    failureReason: pick(x, 'FailureReason', 'failureReason') ?? null,
    paidAt: pick(x, 'PaidAt', 'paidAt') ?? null,
    createdDate: pick(x, 'CreatedDate', 'createdDate') ?? null,
    memberName: pick(x, 'MemberName', 'memberName') ?? null,
    gatewayName: pick(x, 'GatewayName', 'gatewayName') ?? null,
  };
}

function mapReceipt(x: any): ReceiptData {
  const uid = pick<string>(x, 'PaymentUniqueId', 'paymentUniqueId') ?? '';
  return {
    // payment
    paymentId: asNum(pick(x, 'PaymentId', 'paymentId')),
    paymentUniqueId: uid,
    receiptNo: pick<string>(x, 'ReceiptNo', 'receiptNo') ?? uid,
    paymentStatus: pick(x, 'PaymentStatus', 'paymentStatus') ?? '',
    amount: asNum(pick(x, 'Amount', 'amount')),
    currency: pick(x, 'Currency', 'currency') ?? 'INR',
    paymentMode: pick(x, 'PaymentMode', 'paymentMode') ?? null,
    gatewayName: pick(x, 'GatewayName', 'gatewayName') ?? null,
    gatewayOrderId: pick(x, 'GatewayOrderId', 'gatewayOrderId') ?? null,
    gatewayPaymentId: pick(x, 'GatewayPaymentId', 'gatewayPaymentId') ?? null,
    paidAt: pick(x, 'PaidAt', 'paidAt') ?? null,
    createdDate: pick(x, 'CreatedDate', 'createdDate') ?? '',
    // member
    memberId: asNum(pick(x, 'MemberId', 'memberId')),
    memberName: pick(x, 'MemberName', 'memberName') ?? null,
    memberUniqueId: pick(x, 'MemberUniqueId', 'memberUniqueId') ?? null,
    memberEmail: pick(x, 'MemberEmail', 'memberEmail') ?? null,
    memberMobile: pick(x, 'MemberMobile', 'memberMobile') ?? null,
    memberAddress: pick(x, 'MemberAddress', 'memberAddress') ?? null,
    memberCity: pick(x, 'MemberCity', 'memberCity') ?? null,
    memberState: pick(x, 'MemberState', 'memberState') ?? null,
    memberPincode: pick(x, 'MemberPincode', 'memberPincode') ?? null,
    // policy / premium breakdown — GST keys checked across PascalCase/camelCase variants
    masterPolicyName: pick(x, 'MasterPolicyName', 'masterPolicyName') ?? null,
    sumInsured: pickNum(x, 'SumInsured', 'sumInsured'),
    basePremium: pickNum(x, 'BasePremium', 'basePremium'),
    doctorConsultantAMT: pickNum(x, 'DoctorConsultantAMT', 'doctorConsultantAMT', 'doctorConsultantAmt'),
    gstPer: pickNum(x, 'GSTPer', 'gstPer', 'GstPer', 'gSTPer'),
    gstAmt: pickNum(x, 'GSTAmt', 'gstAmt', 'GstAmt', 'gSTAmt'),
    premiumWithGST: pickNum(x, 'PremiumWithGST', 'premiumWithGST'),
    grossAmt: pickNum(x, 'GrossAmt', 'grossAmt'),
    // partner
    partnerName: pick(x, 'PartnerName', 'partnerName') ?? null,
    partnerCode: pick(x, 'PartnerCode', 'partnerCode') ?? null,
    // issuer (may be absent on older backends — the view falls back to the static name)
    issuer: mapIssuer(pick(x, 'Issuer', 'issuer')),
  };
}

function mapIssuer(x: any): ReceiptIssuer | null {
  if (!x || typeof x !== 'object') return null;
  return {
    name: pick(x, 'Name', 'name') ?? 'Arogya Insurance',
    address: pick(x, 'Address', 'address') ?? null,
    email: pick(x, 'Email', 'email') ?? null,
    phone: pick(x, 'Phone', 'phone') ?? null,
    gstin: pick(x, 'Gstin', 'gstin', 'GSTIN') ?? null,
    logoUrl: pick(x, 'LogoUrl', 'logoUrl') ?? null,
  };
}

/** Apply a row-mapper to the `data` of an envelope (array or object), passing other fields through. */
function mapEnv<T>(fn: (x: any) => T) {
  return (res: any) => {
    if (res && Array.isArray(res.data)) return { ...res, data: res.data.map(fn) };
    if (res && res.data && typeof res.data === 'object') return { ...res, data: fn(res.data) };
    return res;
  };
}

@Injectable({ providedIn: 'root' })
export class PaymentService {
  private http = inject(HttpClient);
  private readonly api = environment.apiUrl;

  // ===================== Gateways =====================
  gwGetAll() {
    return this.http
      .get<ApiEnvelope<PaymentGateway[]>>(`${this.api}/PaymentGateway/GetAll`)
      .pipe(map(mapEnv(mapGateway)));
  }
  gwGetById(id: number) {
    return this.http
      .get<ApiEnvelope<PaymentGateway>>(`${this.api}/PaymentGateway/GetById`, { params: { id } })
      .pipe(map(mapEnv(mapGateway)));
  }
  gwSave(m: Partial<PaymentGateway>) {
    const verb = m.id ? 'Update' : 'Insert';
    return this.http.post<ApiEnvelope>(`${this.api}/PaymentGateway/${verb}`, m);
  }
  gwSetActive(id: number, on: boolean) {
    return this.http.get<ApiEnvelope>(`${this.api}/PaymentGateway/${on ? 'Enable' : 'Disable'}`, {
      params: { id },
    });
  }
  gwDelete(id: number) {
    return this.http.get<ApiEnvelope>(`${this.api}/PaymentGateway/Delete`, { params: { id } });
  }

  // ===================== Integrations =====================
  inGetAll() {
    return this.http
      .get<ApiEnvelope<PaymentIntegration[]>>(`${this.api}/PaymentIntegration/GetAll`)
      .pipe(map(mapEnv(mapIntegration)));
  }
  inGetById(id: number) {
    return this.http
      .get<ApiEnvelope<PaymentIntegration>>(`${this.api}/PaymentIntegration/GetById`, { params: { id } })
      .pipe(map(mapEnv(mapIntegration)));
  }
  inGetByPartner(partnerId: number) {
    return this.http
      .get<ApiEnvelope<PaymentIntegration[]>>(`${this.api}/PaymentIntegration/GetByPartner`, {
        params: { partnerId },
      })
      .pipe(map(mapEnv(mapIntegration)));
  }
  inResolve(gatewayId: number, partnerId?: number) {
    const params: any = { gatewayId };
    if (partnerId) params.partnerId = partnerId;
    return this.http
      .get<ApiEnvelope<PaymentIntegration>>(`${this.api}/PaymentIntegration/Resolve`, { params })
      .pipe(map(mapEnv(mapIntegration)));
  }
  inSave(m: Partial<PaymentIntegration>) {
    const verb = m.id ? 'Update' : 'Insert';
    return this.http.post<ApiEnvelope>(`${this.api}/PaymentIntegration/${verb}`, m);
  }
  inSetActive(id: number, on: boolean) {
    return this.http.get<ApiEnvelope>(`${this.api}/PaymentIntegration/${on ? 'Enable' : 'Disable'}`, {
      params: { id },
    });
  }
  inDelete(id: number) {
    return this.http.get<ApiEnvelope>(`${this.api}/PaymentIntegration/Delete`, { params: { id } });
  }

  // ===================== Lookups (PaymentTypeMaster) =====================
  ptByCategory(category: PaymentCategory | string) {
    return this.http
      .get<ApiEnvelope<PaymentType[]>>(`${this.api}/PaymentType/GetByCategory`, { params: { category } })
      .pipe(map(mapEnv(mapType)));
  }
  ptGetAll() {
    return this.http
      .get<ApiEnvelope<PaymentType[]>>(`${this.api}/PaymentType/GetAll`)
      .pipe(map(mapEnv(mapType)));
  }
  ptSave(m: Partial<PaymentType>) {
    const verb = m.id ? 'Update' : 'Insert';
    return this.http.post<ApiEnvelope>(`${this.api}/PaymentType/${verb}`, m);
  }
  ptSetActive(id: number, on: boolean) {
    return this.http.get<ApiEnvelope>(`${this.api}/PaymentType/${on ? 'Enable' : 'Disable'}`, {
      params: { id },
    });
  }
  ptDelete(id: number) {
    return this.http.get<ApiEnvelope>(`${this.api}/PaymentType/Delete`, { params: { id } });
  }

  // ===================== Member payments =====================
  payCreate(m: any) {
    return this.http.post<ApiEnvelope>(`${this.api}/MemberPayment/Create`, m);
  }
  payGetByMember(memberId: number) {
    return this.http
      .get<ApiEnvelope<MemberPayment[]>>(`${this.api}/MemberPayment/GetByMember`, {
        params: { memberId },
      })
      .pipe(map(mapEnv(mapPayment)));
  }
  payGetAll() {
    return this.http
      .get<ApiEnvelope<MemberPayment[]>>(`${this.api}/MemberPayment/GetAll`)
      .pipe(map(mapEnv(mapPayment)));
  }
  payGetByUid(paymentUniqueId: string) {
    return this.http
      .get<ApiEnvelope<MemberPayment>>(`${this.api}/MemberPayment/GetByPaymentUniqueId`, {
        params: { paymentUniqueId },
      })
      .pipe(map(mapEnv(mapPayment)));
  }
  /** One payment's full detail (any status), scoped server-side (admin → any; partner → own). */
  payGetDetail(id: number) {
    return this.http
      .get<ApiEnvelope<MemberPayment>>(`${this.api}/MemberPayment/Detail`, { params: { id } })
      .pipe(map(mapEnv(mapPayment)));
  }
  payUpdateStatus(m: any) {
    return this.http.post<ApiEnvelope>(`${this.api}/MemberPayment/UpdateStatus`, m);
  }

  // ===================== Gateway-agnostic start =====================
  /**
   * Start a collection WITHOUT naming a gateway — the server uses the one the member's partner is
   * assigned. Prefer this over rzpCreateOrder: posting straight to Razorpay meant that assigning a
   * partner the Generali integration changed nothing, because the caller had already decided.
   *
   * The reply carries a `gateway` discriminator; read `razorpay`-shaped fields for RAZORPAY and
   * `postUrl` / `fields` for GENERALI.
   *
   * `paymentOption` and `method` are GENERALI-only and both optional — omit them and the server
   * uses whatever the integration is configured with. See GENERALI_METHOD below for `method`.
   */
  payStart(m: {
    memberId: number;
    paymentMode?: string | null;
    paymentOption?: string | null;
    method?: string | null;
  }) {
    return this.http.post<ApiEnvelope<StartedPayment>>(`${this.api}/MemberPayment/Start`, m);
  }

  /** Which gateway a member's premium would go through right now (for labelling, before Pay). */
  payMethod(memberId: number) {
    return this.http.get<ApiEnvelope<PaymentMethodInfo>>(`${this.api}/MemberPayment/Method`, {
      params: { memberId },
    });
  }

  // ===================== Razorpay =====================
  /** Create a Razorpay order on the backend (records the payment PENDING). */
  rzpCreateOrder(m: CreateRazorpayOrder) {
    return this.http
      .post<ApiEnvelope<RazorpayCheckout>>(`${this.api}/Razorpay/CreateOrder`, m)
      .pipe(map(mapEnv(mapRzpCheckout)));
  }

  /**
   * Verify the checkout handshake — this is what flips the member to PAID.
   *
   * Razorpay has ALREADY captured the money by the time this runs, so a single
   * best-effort POST is not enough: one dropped request leaves the gateway paid
   * and our MemberPayments row stuck on PENDING. Retry a few times with a short
   * backoff before giving up, and let the caller report the payment id rather
   * than implying nothing was charged.
   */
  rzpVerify(m: { razorpayOrderId: string; razorpayPaymentId: string; razorpaySignature: string }) {
    return this.http.post<ApiEnvelope>(`${this.api}/Razorpay/Verify`, m).pipe(
      retry({ count: 3, delay: (_err, attempt) => timer(attempt * 2000) }),
    );
  }

  // ===================== Payment receipts (api/PaymentReceipt) =====================
  // Data* → JSON envelope (any status); Html* → text/html (SUCCESS only);
  // Download* → application/pdf blob (SUCCESS only, 500 if the PDF engine is missing).
  // NOTE: never point an <a href>/window.open/<iframe> at these URLs — the JWT wouldn't be
  // attached. Always go through HttpClient (the interceptor adds Authorization), then blob it.

  /** JSON receipt data — works for ANY status; render the on-screen receipt from this. */
  rcData(paymentId: number) {
    return this.http
      .get<ApiEnvelope<ReceiptData>>(`${this.api}/PaymentReceipt/Data`, { params: { paymentId } })
      .pipe(map(mapEnv(mapReceipt)));
  }
  rcDataByUid(paymentUniqueId: string) {
    return this.http
      .get<ApiEnvelope<ReceiptData>>(`${this.api}/PaymentReceipt/DataByUniqueId`, {
        params: { paymentUniqueId },
      })
      .pipe(map(mapEnv(mapReceipt)));
  }

  /** Printable HTML (SUCCESS only → 400 otherwise). responseType 'text'. */
  rcHtml(paymentId: number) {
    return this.http.get(`${this.api}/PaymentReceipt/Html`, { params: { paymentId }, responseType: 'text' });
  }
  rcHtmlByUid(paymentUniqueId: string) {
    return this.http.get(`${this.api}/PaymentReceipt/HtmlByUniqueId`, {
      params: { paymentUniqueId },
      responseType: 'text',
    });
  }

  /** PDF bytes (SUCCESS only → 400; 500 only on an unexpected render error — the engine is
   *  server-side DinkToPdf with the native lib bundled, so no host install needed). responseType 'blob'. */
  rcPdf(paymentId: number) {
    return this.http.get(`${this.api}/PaymentReceipt/Download`, { params: { paymentId }, responseType: 'blob' });
  }
  rcPdfByUid(paymentUniqueId: string) {
    return this.http.get(`${this.api}/PaymentReceipt/DownloadByUniqueId`, {
      params: { paymentUniqueId },
      responseType: 'blob',
    });
  }
}
